/**
 * Finance ownership and quantity derivation inputs.
 *
 * Contract: docs/source-of-truth/worker-service-delivery-architecture.md
 * ("v1/v2 single-owner finance handover" and "Execution mode to quantity
 * derivation").
 *
 * This module owns one invariant: **an occurrence has exactly one finance
 * owner, for its whole life.** Everything else here exists to make that
 * checkable. It deliberately contains no prices, rates or amounts — it decides
 * *who may post* and *what operational fact feeds the quantity*, never *how
 * much*.
 */

import type { ExecutionMode, QuantitySource } from './execution-modes';
import { quantitySourceForMode, requiresAllocationInvariant } from './execution-modes';

// ---------------------------------------------------------------------------
// Owner
// ---------------------------------------------------------------------------

/**
 * Stamped on an occurrence at creation and immutable thereafter.
 *
 * `V1` continues through the existing
 * `complete_sah_service_delivery` / `sah_service_details` /
 * `apply_sah_service_financials` path, including the existing
 * `record_sah_deemed_delivery` spine for a deemed approval.
 *
 * `V2` commits operational evidence plus an outbox event and must never call
 * the v1 financial upsert.
 */
export const FINANCE_PROJECTION_VERSIONS = ['V1', 'V2'] as const;

export type FinanceProjectionVersion = (typeof FINANCE_PROJECTION_VERSIONS)[number];

export function isFinanceProjectionVersion(
  value: string | null | undefined,
): value is FinanceProjectionVersion {
  return value === 'V1' || value === 'V2';
}

/**
 * Whether the v2 projector may actually post, as opposed to running in
 * comparison mode. Shadow is the default and the safe state.
 */
export const PROJECTION_MODES = ['SHADOW', 'POSTING'] as const;

export type ProjectionMode = (typeof PROJECTION_MODES)[number];

// ---------------------------------------------------------------------------
// Lifecycle
// ---------------------------------------------------------------------------

/**
 * The occurrence states that matter to finance ownership. An occurrence that
 * has committed evidence can no longer change hands.
 */
export const OCCURRENCE_FINANCE_STATES = [
  'NOT_STARTED',
  'STARTED',
  'EVIDENCE_COMMITTED',
  'FINANCIALLY_PROJECTED',
] as const;

export type OccurrenceFinanceState = (typeof OCCURRENCE_FINANCE_STATES)[number];

export interface FinanceOwnership {
  occurrenceId: string;
  tenantId: string;
  /** Immutable once set. */
  owner: FinanceProjectionVersion;
  state: OccurrenceFinanceState;
  /** Tenant-scoped unique key proving one source occurrence owns one posting. */
  sourceOccurrenceKey: string;
  /** True when a v1 detail or financial event already owns this occurrence. */
  v1FinancialRecordExists: boolean;
}

// ---------------------------------------------------------------------------
// Rollback
// ---------------------------------------------------------------------------

export interface OwnerChangeDecision {
  allowed: boolean;
  reasonCode: string;
}

/**
 * Rollback moves an occurrence's owner only while it has not started.
 *
 * Once evidence is committed, the occurrence stays with v2 and is resolved
 * through v2 reconciliation. Reposting it through v1 would either fabricate a
 * v1 generic completion it never had, or post it twice.
 */
export function canChangeFinanceOwner(
  ownership: FinanceOwnership,
  nextOwner: FinanceProjectionVersion,
): OwnerChangeDecision {
  // Fails closed on a value that is not a known version. These arrive from the
  // database and from replayed payloads, where the TypeScript union is no
  // guarantee.
  if (!isFinanceProjectionVersion(ownership.owner)) {
    return { allowed: false, reasonCode: 'unknown_current_owner' };
  }
  if (!isFinanceProjectionVersion(nextOwner)) {
    return { allowed: false, reasonCode: 'unknown_next_owner' };
  }
  if (!OCCURRENCE_FINANCE_STATES.includes(ownership.state)) {
    return { allowed: false, reasonCode: 'unknown_occurrence_state' };
  }
  if (ownership.owner === nextOwner) {
    return { allowed: true, reasonCode: 'owner_unchanged' };
  }
  // A v1 financial record already owns this occurrence's money, so handing it
  // to v2 would orphan that posting even though nothing has started.
  if (ownership.v1FinancialRecordExists) {
    return { allowed: false, reasonCode: 'v1_financial_record_already_owns_occurrence' };
  }
  if (ownership.state !== 'NOT_STARTED') {
    return { allowed: false, reasonCode: `owner_frozen_after_${ownership.state.toLowerCase()}` };
  }
  return { allowed: true, reasonCode: 'owner_changed_before_start' };
}

// ---------------------------------------------------------------------------
// Posting eligibility
// ---------------------------------------------------------------------------

export interface PostingDecision {
  mayPost: boolean;
  reasonCode: string;
}

/**
 * Whether the v2 projector may post for this occurrence.
 *
 * Fails closed on every uncertainty. The `v1FinancialRecordExists` check is the
 * one that prevents a double post during migration: if v1 already owns the
 * money for this occurrence, v2 must never also post it, whatever the flags
 * say.
 *
 * Note that the pack-render and resolver flags are deliberately *not* inputs.
 * The single-owner invariant has to hold independently of them, so that turning
 * a UI flag on or off can never duplicate or orphan money.
 */
export function mayV2Post(ownership: FinanceOwnership, mode: ProjectionMode): PostingDecision {
  if (mode !== 'POSTING') {
    return { mayPost: false, reasonCode: 'projector_in_shadow_mode' };
  }
  if (ownership.owner !== 'V2') {
    return { mayPost: false, reasonCode: 'occurrence_owned_by_v1' };
  }
  if (ownership.v1FinancialRecordExists) {
    return { mayPost: false, reasonCode: 'v1_financial_record_already_owns_occurrence' };
  }
  if (!ownership.sourceOccurrenceKey) {
    return { mayPost: false, reasonCode: 'missing_source_occurrence_key' };
  }
  if (ownership.state !== 'EVIDENCE_COMMITTED') {
    // Includes FINANCIALLY_PROJECTED: posting twice is the thing being
    // prevented, so an already-projected occurrence is not eligible again.
    return { mayPost: false, reasonCode: `state_not_eligible:${ownership.state}` };
  }
  return { mayPost: true, reasonCode: 'eligible' };
}

/**
 * Whether the v1 path may post.
 *
 * Carries the same state discipline as `mayV2Post`. An earlier version only
 * checked ownership, which let v1 post an occurrence that had not started and
 * let it post one that had already been projected. The mutual-exclusion test
 * did not catch it, because "v1 and v2 are never both eligible" stays true
 * while v1 alone is wrong.
 */
export function mayV1Post(ownership: FinanceOwnership): PostingDecision {
  if (ownership.owner !== 'V1') {
    return { mayPost: false, reasonCode: 'occurrence_owned_by_v2' };
  }
  if (ownership.v1FinancialRecordExists) {
    return { mayPost: false, reasonCode: 'v1_financial_record_already_posted' };
  }
  if (!ownership.sourceOccurrenceKey) {
    return { mayPost: false, reasonCode: 'missing_source_occurrence_key' };
  }
  if (ownership.state !== 'EVIDENCE_COMMITTED') {
    return { mayPost: false, reasonCode: `state_not_eligible:${ownership.state}` };
  }
  return { mayPost: true, reasonCode: 'eligible' };
}

// ---------------------------------------------------------------------------
// Quantity derivation inputs
// ---------------------------------------------------------------------------

/**
 * The operational facts the server needs to derive a quantity, per mode.
 *
 * Every field is something that happened in the real world. There is
 * deliberately no price, rate, category or funding field: the projector reads
 * those from the catalogue and the provider agreement.
 */
export interface QuantityDerivationInput {
  mode: ExecutionMode;
  source: QuantitySource;
  /** Set only for modes that allocate one client service across subjects. */
  allocationSubjectCount?: number;
  requiresAllocationProof: boolean;
}

export function quantityDerivationInputFor(
  mode: ExecutionMode,
  allocationSubjectCount?: number,
): QuantityDerivationInput {
  return {
    mode,
    source: quantitySourceForMode(mode),
    ...(allocationSubjectCount === undefined ? {} : { allocationSubjectCount }),
    requiresAllocationProof: requiresAllocationInvariant(mode),
  };
}

export interface AllocationIssue {
  mode: ExecutionMode;
  issue: string;
}

/**
 * Group and multi-worker occurrences allocate one client service across
 * several subjects. Proving the allocation exists and is positive is what stops
 * a cloned assignment turning into duplicate claims.
 */
export function findAllocationIssues(input: QuantityDerivationInput): AllocationIssue[] {
  const issues: AllocationIssue[] = [];
  if (!input.requiresAllocationProof) {
    if (input.allocationSubjectCount !== undefined) {
      issues.push({ mode: input.mode, issue: 'allocation_supplied_for_unallocated_mode' });
    }
    return issues;
  }
  if (input.allocationSubjectCount === undefined) {
    issues.push({ mode: input.mode, issue: 'missing_allocation_subject_count' });
    return issues;
  }
  if (!Number.isInteger(input.allocationSubjectCount) || input.allocationSubjectCount < 1) {
    issues.push({ mode: input.mode, issue: 'invalid_allocation_subject_count' });
  }
  return issues;
}
